import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
export const ImageFrame = styled.div `
  display: inline-block;
  overflow: hidden;
  position: relative;
  width: ${props => (props.width ? props.width + 'px' : 'auto')};
  height: ${props => (props.height ? props.height + 'px' : 'auto')};
`;
export const ImagePlaceholder = styled.div `
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: inherit;
  opacity: 1;
  background-image: url(${props => props.placeholder});
  background-size: 100%;
  background-repeat: no-repeat;
  filter: blur(20px);
  z-index: 3;
  &.loaded {
    ${props => props.fade &&
    css `
        transition: opacity 0.5s linear;
      `};
    opacity: 0;
  }
`;
export const DivWithBackgroundImage = styled.div `
  background-image: url(${props => props.imageSource});
  background-size: ${props => props.size};
  background-repeat: no-repeat;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  opacity: 0;
  &.loaded {
    ${props => props.fade &&
    css `
        transition: opacity 0.5s linear;
      `};
    opacity: 1;
  }
`;
/**
 * Basic image component.
 * Can render a placeholder (Base64 image as string) while image source has not been loaded.
 */
class Image extends React.PureComponent {
    constructor(props) {
        super(props);
        /**
         * Initial state
         * @type {{imageLoaded: boolean}}
         */
        this.state = {
            hasBeenLoaded: false,
        };
    }
    render() {
        const { className, width, height, size, placeholder, fade = false, src, alt, dataTestId, } = this.props;
        const { hasBeenLoaded } = this.state;
        return (React.createElement(ImageFrame, { className: className, width: this.props.width, height: this.props.height, "data-testid": dataTestId }, size || placeholder ? (React.createElement(React.Fragment, null,
            placeholder ? (React.createElement(ImagePlaceholder, { placeholder: placeholder, className: hasBeenLoaded ? 'loaded' : 'loading', fade: fade })) : null,
            React.createElement(DivWithBackgroundImage, { imageSource: src, size: size, className: hasBeenLoaded ? 'loaded' : 'loading', fade: fade }),
            React.createElement("img", { src: src, alt: alt, style: { display: 'none' }, width: width, height: height, onLoad: () => {
                    this.setState({ hasBeenLoaded: true });
                } }))) : (React.createElement("img", { src: src, alt: alt, width: width, height: height }))));
    }
}
Image.propTypes = {
    /**
     * Allow using styled() class on the input wrapper. Don't use this to set a class manually!
     */
    className: PropTypes.string,
    /**
     * Image source
     */
    src: PropTypes.string.isRequired,
    /**
     * Image alt tag
     */
    alt: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /**
     * Should be a base64 encoded image as a string. For example a scaled down version of the image source.
     * Generating those base64 strings can be achieved on the server side or a build step which inlines images with a certain prefix.
     */
    placeholder: PropTypes.string,
    /**
     * See background-size css property for examples
     */
    size: PropTypes.oneOf(['contain', 'cover']),
    /**
     * If true fades the placeholder image into the actual image.
     */
    fade: PropTypes.bool,
    dataTestId: PropTypes.string,
};
Image.defaultProps = {
    alt: '',
    className: '',
    fade: false,
    width: null,
    height: null,
    placeholder: null,
    size: 'cover',
    dataTestId: null,
};
export { Image };
